import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { stopImpersonation } from './impersonation-api';
import {
  markImpersonationJustEnded,
  readReturnPath,
  clearReturnPath,
  consumeImpersonationJustEnded,
} from './impersonation-storage';
import { enqueueNotification } from '../../../store/notification/action';
import { impersonationSelector } from '../../../new-store/impersonation/selectors';
import styles from './impersonation-banner.module.scss';

// Reads the global impersonation state (populated once on app bootstrap by
// fetchImpersonation) and renders the persistent read-only banner.
const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isImpersonating, target } = useSelector(impersonationSelector);

  // Fire the end-of-session toast once, if we just came back from a stop reload.
  useEffect(() => {
    if (consumeImpersonationJustEnded()) {
      dispatch(
        enqueueNotification(
          t('impersonation.stopSuccess', 'Impersonation session ended'),
          'success',
        ),
      );
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push the main view down while the (sticky, in-topbar) banner is shown, so it
  // never overlaps the page content. Scoped to <body> so any layout benefits.
  useEffect(() => {
    document.body.classList.toggle('impersonating', isImpersonating);
    return () => document.body.classList.remove('impersonating');
  }, [isImpersonating]);

  if (!isImpersonating || !target) {
    return null;
  }

  const handleStop = async () => {
    try {
      await stopImpersonation();
      // Only flag "just ended" + reload on a real stop, so the toast never
      // fires while the server still reports an active session.
      markImpersonationJustEnded();
      const returnPath = readReturnPath() || '/';
      clearReturnPath();
      window.location = (window.location.origin + returnPath) as any;
    } catch {
      dispatch(
        enqueueNotification(
          t('impersonation.stopError', 'Could not stop the impersonation session'),
        ),
      );
    }
  };

  return (
    <div className={styles.banner}>
      <span>
        {t('impersonation.bannerMessage', {
          name: target.name,
          wwid: target.wwid,
          defaultValue: 'You are impersonating {{name}} – {{wwid}}.',
        })}
      </span>
      <Button size="small" variant="outlined" className={styles.stop} onClick={handleStop}>
        {t('impersonation.stopButton', 'Stop Impersonation')}
      </Button>
    </div>
  );
};

export default ImpersonationBanner;
